import { match } from 'ts-pattern';
import { z } from 'zod';

import { type B2bSavingPlanCategory, B2bSavingPlanCategoryValues } from '@Common/types/b2b/plans';

export const ZodB2BEarlyWithdrawalReason = z.enum([
  'marriage',
  'conclusion_of_a_pacs',
  'divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s',
  'birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child',
  'invalidity',
  'savers_death',
  'death_of_the_pacs_spouse_or_partner',
  'over_indebtedness',
  'domestic_violence',
  'creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs',
  'cessation_of_self_employed_activity',
  'termination_of_the_employment_contract_or_activity_of_individual_entrepreneur',
  'expiration_of_unemployment_insurance_rights',
  'leaving_for_retirement',
  'acquisition_of_the_main_residence',
  'construction_of_the_main_residence',
  'expansion_of_the_main_residence',
  'rehabilitation_following_a_natural_disaster',
  'no_employment_contract_or_corporate_mandate',
  'termination_of_the_social_mandate',
  'energy_renovation',
  'acquisition_of_shares_in_a_production_cooperative',
  'judicial_liquidation_or_full_transfer_of_the_company',
  'purchase_of_a_low_emission_vehicle',
  'caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner',
  'option_exercise',
  'instrument_maturity',
  'indirect_management',
]);

export type B2BEarlyWithdrawalReason = z.infer<typeof ZodB2BEarlyWithdrawalReason>;

export const ZodB2BEarlyWithdrawalReasonAppCreatable = ZodB2BEarlyWithdrawalReason.exclude([
  'option_exercise',
  'instrument_maturity',
  'indirect_management',
  'savers_death',
]);

export type B2BEarlyWithdrawalReasonAppCreatable = z.infer<
  typeof ZodB2BEarlyWithdrawalReasonAppCreatable
>;

export type B2BEarlyWithdrawalEventDateRule = 'invalid' | 'six_months' | 'unrestricted' | 'no_date';

export type B2BEarlyWithdrawalEventDateRulesByReason = Record<
  B2BEarlyWithdrawalReason,
  B2BEarlyWithdrawalEventDateRule
>;

const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_INVALID = {
  marriage: 'invalid',
  conclusion_of_a_pacs: 'invalid',
  divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s: 'invalid',
  birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child: 'invalid',
  invalidity: 'invalid',
  savers_death: 'invalid',
  death_of_the_pacs_spouse_or_partner: 'invalid',
  over_indebtedness: 'invalid',
  domestic_violence: 'invalid',
  creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs:
    'invalid',
  cessation_of_self_employed_activity: 'invalid',
  termination_of_the_employment_contract_or_activity_of_individual_entrepreneur: 'invalid',
  expiration_of_unemployment_insurance_rights: 'invalid',
  leaving_for_retirement: 'invalid',
  acquisition_of_the_main_residence: 'invalid',
  construction_of_the_main_residence: 'invalid',
  expansion_of_the_main_residence: 'invalid',
  rehabilitation_following_a_natural_disaster: 'invalid',
  no_employment_contract_or_corporate_mandate: 'invalid',
  termination_of_the_social_mandate: 'invalid',
  energy_renovation: 'invalid',
  acquisition_of_shares_in_a_production_cooperative: 'invalid',
  judicial_liquidation_or_full_transfer_of_the_company: 'invalid',
  purchase_of_a_low_emission_vehicle: 'invalid',
  caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner: 'invalid',
  option_exercise: 'invalid',
  instrument_maturity: 'invalid',
  indirect_management: 'invalid',
} as const satisfies B2BEarlyWithdrawalEventDateRulesByReason;

const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PEI = {
  ...B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_INVALID,
  marriage: 'six_months',
  conclusion_of_a_pacs: 'six_months',
  divorce_or_separation_or_dissolution_of_a_pacs_with_dependent_child_s: 'six_months',
  birth_or_adoption_of_the_3rd_child_then_of_each_subsequent_child: 'six_months',
  invalidity: 'six_months',
  savers_death: 'unrestricted',
  death_of_the_pacs_spouse_or_partner: 'unrestricted',
  over_indebtedness: 'unrestricted',
  domestic_violence: 'unrestricted',
  creation_or_takeover_of_a_business_by_the_saver_or_his_children_or_his_spouse_or_partner_of_pacs:
    'six_months',
  cessation_of_self_employed_activity: 'unrestricted',
  termination_of_the_employment_contract_or_activity_of_individual_entrepreneur: 'unrestricted',
  leaving_for_retirement: 'unrestricted',
  acquisition_of_the_main_residence: 'six_months',
  construction_of_the_main_residence: 'six_months',
  expansion_of_the_main_residence: 'six_months',
  rehabilitation_following_a_natural_disaster: 'six_months',
  no_employment_contract_or_corporate_mandate: 'unrestricted',
  termination_of_the_social_mandate: 'unrestricted',
  energy_renovation: 'six_months',
  acquisition_of_shares_in_a_production_cooperative: 'six_months',
  purchase_of_a_low_emission_vehicle: 'six_months',
  caregiver_activity_exercised_by_the_holder_or_their_spouse_or_pacs_partner: 'six_months',
  option_exercise: 'unrestricted',
  instrument_maturity: 'unrestricted',
  indirect_management: 'unrestricted',
} as const satisfies B2BEarlyWithdrawalEventDateRulesByReason;

const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERECOI = {
  ...B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_INVALID,
  invalidity: 'unrestricted',
  savers_death: 'unrestricted',
  death_of_the_pacs_spouse_or_partner: 'unrestricted',
  over_indebtedness: 'unrestricted',
  expiration_of_unemployment_insurance_rights: 'unrestricted',
  leaving_for_retirement: 'unrestricted',
  acquisition_of_the_main_residence: 'six_months',
  construction_of_the_main_residence: 'six_months',
  termination_of_the_social_mandate: 'unrestricted',
  judicial_liquidation_or_full_transfer_of_the_company: 'six_months',
  option_exercise: 'unrestricted',
  instrument_maturity: 'unrestricted',
  indirect_management: 'unrestricted',
} as const satisfies B2BEarlyWithdrawalEventDateRulesByReason;

const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERCO = {
  ...B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERECOI,
  rehabilitation_following_a_natural_disaster: 'six_months',
  termination_of_the_social_mandate: 'invalid',
  judicial_liquidation_or_full_transfer_of_the_company: 'invalid',
} as const satisfies B2BEarlyWithdrawalEventDateRulesByReason;

const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_ARTICLE = {
  ...B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_INVALID,
  expiration_of_unemployment_insurance_rights: 'no_date',
  judicial_liquidation_or_full_transfer_of_the_company: 'no_date',
  invalidity: 'no_date',
  savers_death: 'no_date',
  death_of_the_pacs_spouse_or_partner: 'no_date',
  over_indebtedness: 'no_date',
  leaving_for_retirement: 'no_date',
} as const satisfies B2BEarlyWithdrawalEventDateRulesByReason;

const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PER = {
  ...B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_ARTICLE,
  acquisition_of_the_main_residence: 'no_date',
  construction_of_the_main_residence: 'no_date',
} as const satisfies B2BEarlyWithdrawalEventDateRulesByReason;

export const B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_BY_PLAN_CATEGORY = {
  PEI: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PEI,
  PEE: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PEI,
  PEEG: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PEI,
  PERECOI: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERECOI,
  PERECO: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERECOI,
  PERECOG: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERECOI,
  PERCOI: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERCO,
  PERCO: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PERCO,
  PER: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PER,
  PERO: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PER,
  PERU: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PER,
  PERECOER: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_PER,
  ARTICLE_82: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_ARTICLE,
  ARTICLE_83: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_ARTICLE,
  ER_EXTERNAL: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_INVALID,
} as const satisfies Record<B2bSavingPlanCategory, B2BEarlyWithdrawalEventDateRulesByReason>;

export type B2BEarlyWithdrawalEventDateRulesByPlanCategory = Record<
  B2bSavingPlanCategory,
  B2BEarlyWithdrawalEventDateRulesByReason
>;

export type B2BEarlyWithdrawalPolicy = {
  eventDateRulesByPlanCategory: B2BEarlyWithdrawalEventDateRulesByPlanCategory;
};

export const DEFAULT_B2B_EARLY_WITHDRAWAL_POLICY: B2BEarlyWithdrawalPolicy = {
  eventDateRulesByPlanCategory: B2B_EARLY_WITHDRAWAL_EVENT_DATE_RULES_BY_PLAN_CATEGORY,
};

export function withEarlyWithdrawalEventDateRulePatches(
  base: B2BEarlyWithdrawalPolicy,
  patches: Partial<
    Record<
      B2bSavingPlanCategory,
      Partial<Record<B2BEarlyWithdrawalReason, B2BEarlyWithdrawalEventDateRule>>
    >
  >,
): B2BEarlyWithdrawalPolicy {
  const eventDateRulesByPlanCategory: B2BEarlyWithdrawalEventDateRulesByPlanCategory = {
    ...base.eventDateRulesByPlanCategory,
  };

  for (const planCategory of Object.keys(patches) as B2bSavingPlanCategory[]) {
    const reasonPatches = patches[planCategory];
    if (!reasonPatches) {
      continue;
    }

    eventDateRulesByPlanCategory[planCategory] = {
      ...eventDateRulesByPlanCategory[planCategory],
      ...reasonPatches,
    };
  }

  return { eventDateRulesByPlanCategory };
}

export type B2BWithdrawalInitiator = 'app' | 'bo' | 'campaign';

/** Reasons only campaigns (or explicit system callers) may use — blocked for app and BO. */
export const CAMPAIGN_ONLY_REASONS = ['instrument_maturity'] as const;

export const APP_DISABLED_REASONS = [
  'savers_death',
  'option_exercise',
  ...CAMPAIGN_ONLY_REASONS,
] as const;

function buildInvalidReasonPatches(
  reasons: readonly B2BEarlyWithdrawalReason[],
): Partial<
  Record<
    B2bSavingPlanCategory,
    Partial<Record<B2BEarlyWithdrawalReason, B2BEarlyWithdrawalEventDateRule>>
  >
> {
  const reasonRules: Partial<Record<B2BEarlyWithdrawalReason, B2BEarlyWithdrawalEventDateRule>> =
    Object.fromEntries(reasons.map((reason) => [reason, 'invalid']));

  return Object.fromEntries(B2bSavingPlanCategoryValues.map((category) => [category, reasonRules]));
}

export const APP_B2B_EARLY_WITHDRAWAL_POLICY: B2BEarlyWithdrawalPolicy =
  withEarlyWithdrawalEventDateRulePatches(
    DEFAULT_B2B_EARLY_WITHDRAWAL_POLICY,
    buildInvalidReasonPatches(APP_DISABLED_REASONS),
  );

export const BO_B2B_EARLY_WITHDRAWAL_POLICY: B2BEarlyWithdrawalPolicy =
  withEarlyWithdrawalEventDateRulePatches(
    DEFAULT_B2B_EARLY_WITHDRAWAL_POLICY,
    buildInvalidReasonPatches(CAMPAIGN_ONLY_REASONS),
  );

export function earlyWithdrawalPolicyForInitiator(
  initiator: B2BWithdrawalInitiator,
): B2BEarlyWithdrawalPolicy {
  return match(initiator)
    .with('app', () => APP_B2B_EARLY_WITHDRAWAL_POLICY)
    .with('bo', () => BO_B2B_EARLY_WITHDRAWAL_POLICY)
    .with('campaign', () => DEFAULT_B2B_EARLY_WITHDRAWAL_POLICY)
    .exhaustive();
}
